import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ImageThumb.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ImageThumbProps {
  /** `grid` = 96x64 (message image grid) · `strip` = 76x52 (tool thumbnail strip). */
  size: 'grid' | 'strip';
  /** Placeholder colour shown until real media exists. Any CSS colour. */
  fill?: string;
  src?: string;
  /**
   * Alternative text for `src`. Left empty by default, which marks the thumbnail decorative —
   * correct when the surrounding message already describes it, wrong when it does not, so pass a
   * real description whenever the image carries meaning.
   */
  alt?: string;
  theme?: ChatTheme;
  className?: string;
}

/**
 * One media rectangle: a rounded box that is either a real image or the flat placeholder colour
 * the design ships in its place.
 *
 * Both sizes come straight from the files and neither is derived from the other — `grid` is 3:2
 * and sized to fit three across a 304px cap, `strip` is 19:13 and sized to fit three across a
 * tool card. The component draws no border, no shadow and no state: a thumbnail in this design is
 * the picture and nothing else.
 */
export function ImageThumb({
  size,
  fill,
  src,
  alt = '',
  theme: themeProp,
  className,
}: ImageThumbProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(chatThemeClass(theme), styles.thumb, styles[size], className)}
      // The placeholder colour is the only runtime value, so the only thing passed inline.
      style={fill ? ({ '--cv15-thumb-fill': fill } as React.CSSProperties) : undefined}
    >
      {src ? <img className={styles.media} src={src} alt={alt} /> : null}
    </div>
  );
}
